import React, { createContext, useCallback, useContext, useState } from "react";

import { useApi } from "@/hooks/useApi";
import {
  ChatHistoryItem,
  DeleteChatByIdResult,
  GetChatByIdResult,
  GetUserChatHistoryResult,
  UpdateChatBody,
  UpdateChatByIdResult,
} from "@/types/chat-history";

import { useNotifications } from "./NotificationsContext";

export interface ChatHistoryContextType {
  items: ChatHistoryItem[];
  loading: boolean;
  loadChatHistory: () => Promise<void>;
  getChatById: (chatId: string) => Promise<GetChatByIdResult | null>;
  updateChatTitle: (
    chatId: string,
    title: string,
    pinned: boolean
  ) => Promise<void>;
  updateChatPinned: (chatId: string, pinned: boolean) => Promise<void>;
  deleteChatById: (chatId: string) => Promise<void>;
}

const ChatHistoryContext = createContext<ChatHistoryContextType | undefined>(
  undefined
);

interface ChatHistoryProviderProps {
  children: React.ReactNode;
}

export const ChatHistoryProvider: React.FC<ChatHistoryProviderProps> = ({
  children,
}) => {
  const [items, setItems] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  const { apiGet, apiPatch, apiDelete } = useApi();

  const loadChatHistory = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiGet<GetUserChatHistoryResult>("/chat-history");
      if (response?.items) {
        setItems(response.items);
      } else {
        notifyError(
          "Chat History Error.",
          "No chat history seems to be available. Please try again later."
        );
      }
    } catch (e) {
      console.error(e);
      notifyError(
        "Chat History Error.",
        "An error occurred while loading Chat History. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  const getChatById = useCallback(
    async (chatId: string): Promise<GetChatByIdResult | null> => {
      // Don't affect loading when getting a chat.

      try {
        const response = await apiGet<GetChatByIdResult>(
          `/chat-history/${chatId}`
        );
        return response;
      } catch (e) {
        console.error(e);
        notifyError(
          "Chat Details Error.",
          "An error occurred while loading the chat. Please try again later."
        );
        return null;
      }
    },
    [apiGet]
  );

  const updateChatById = useCallback(
    async (chatId: string, body: UpdateChatBody): Promise<void> => {
      setLoading(true);

      try {
        const response = await apiPatch<UpdateChatByIdResult>(
          `/chat-history/${chatId}`,
          body
        );

        if (response) {
          loadChatHistory();
        }
      } catch (e) {
        console.error(e);
        notifyError(
          "Chat Details Error.",
          "An error occurred while updating the chat. Please try again later."
        );
        setLoading(false);
      }
    },
    [apiPatch, loadChatHistory]
  );

  const updateChatTitle = useCallback(
    (chatId: string, title: string, pinned: boolean): Promise<void> =>
      updateChatById(chatId, { title, pinned }),
    [updateChatById]
  );

  const updateChatPinned = useCallback(
    (chatId: string, pinned: boolean): Promise<void> =>
      updateChatById(chatId, { pinned }),
    [updateChatById]
  );

  const deleteChatById = useCallback(
    async (chatId: string): Promise<void> => {
      setLoading(true);

      try {
        const response = await apiDelete<DeleteChatByIdResult>(
          `/chat-history/${chatId}`
        );
        if (response) {
          loadChatHistory();
        }
      } catch (e) {
        console.error(e);
        notifyError(
          "Chat Details Error.",
          "An error occurred while deleting the chat. Please try again later."
        );
        setLoading(false);
      }
    },
    [apiDelete, loadChatHistory]
  );

  return (
    <ChatHistoryContext.Provider
      value={{
        items,
        loading: loading,
        loadChatHistory,
        getChatById,
        updateChatTitle,
        updateChatPinned,
        deleteChatById,
      }}
    >
      {children}
    </ChatHistoryContext.Provider>
  );
};

export const useChatHistory = () => {
  const context = useContext(ChatHistoryContext);
  if (context === undefined) {
    throw new Error("useChatHistory must be used within a ChatHistoryProvider");
  }
  return context;
};
